import React, { useState } from "react"
import styled from "styled-components"
import { themeGet } from "@styled-system/theme-get"
import Heading from "./_UI/Heading"
import Text from "./Text/index"
import NextImg from "./NextImg/index"
import { theme } from "@/common/Mixin"
import BlogArrow from "./_Icon/BlogArrow"
import { useTranslation } from "next-i18next"
import Link from "next/link"

export interface BlogCardProps {
  img: any
  title: string
  desc: string
  tags?: string[]
  date?: string
  link?: string
  onClick?: () => void
  arrow?: boolean
}

export default function BlogCard({ link, ...props }: BlogCardProps) {
  const content = <BlogCardWrapper {...props} />
  if (link != null) {
    return (
      <Link href={link} className="cardWrapper">
        {content}
      </Link>
    )
  }

  return content
}

function BlogCardWrapper({
  img,
  title,
  desc,
  tags,
  date,
  onClick,
  arrow
}: BlogCardProps) {
  const [Hover, setHover] = useState(false)
  const { t } = useTranslation(["walletAsAService"])

  return (
    <Wrapper
      onClick={onClick}
      onMouseEnter={() => {
        setHover(true)
      }}
      onMouseLeave={() => {
        setHover(false)
      }}
    >
      <ImageWrapper className="blogImg">
        <NextImg src={img} priority />
      </ImageWrapper>
      <BlogCardContent className="blogTextWrapper">
        <Heading as="h4" content={title} />
        <div className="line" />
        <Text content={desc} className={"desc"} />
        <CardFooter className="cardFooter">
          {tags && tags.length !== 0 && (
            <div className="tags">
              {tags?.map(tag => (
                <div key={tag} className="tag">
                  #{tag}
                </div>
              ))}
            </div>
          )}
          {date && <p className="date">{date}</p>}
          {arrow && (
            <IconWrapper>
              <Text content={t("button.more")} />
              <BlogArrow className={Hover === true ? "hover" : ""} />
            </IconWrapper>
          )}
        </CardFooter>
      </BlogCardContent>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  flex-wrap: wrap;
  position: relative;
  cursor: pointer;
  min-height: 400px;
  transition: 0.3s;
  display: block;
  border-radius: var(--cardBORAsmall);
  overflow: hidden;
  position: relative;
  border: 1px solid ${themeGet("colors.background")};
  @media screen and (max-width: 1024px) {
    margin-bottom: 1rem;
    flex: 0 1 100%;
  }
  @media (${theme.sm}) {
    border: 0px;
    border-radius: 0;
    margin-bottom: 2rem;
    min-height: 240px;
    h4 {
      text-align: left;
      justify-content: flex-start;
      font-size: 24px;
    }
  }

  @media screen and (max-width: 768px) {
  }

  h3 {
    color: ${theme.color.textContrast};
  }

  &:hover {
    border: 1px solid ${themeGet("colors.primary")};
    @media (${theme.sm}) {
      border: none;
    }
    .blogTextWrapper {
      background-position: 100% -80px;
    }
    h3 {
      color: ${themeGet("colors.primary")};
    }

    .desc {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      text-overflow: ellipsis;
      height: 80px;
      margin: 0;
      margin-top: 8px;
      color: #fff;
      @media (${theme.sm}) {
        height: 80px;
      }
    }

    .line {
      width: 100%;
      height: 2px;
    }
  }
`

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: var(--cardBORAsmall);
  img {
    transition: 0.5s ease;
    z-index: 1;
  }
  @media (${theme.sm}) {
    padding-bottom: 0;
  }
`
const BlogCardContent = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: end;
  align-items: space-between;
  left: 0;
  bottom: 0;
  z-index: 3;
  padding: 1rem;
  height: 100%;
  width: 100%;
  background: linear-gradient(
    to top,
    ${themeGet("colors.background")} 40px,
    ${themeGet("colors.background")}ee 180px,
    rgba(255, 255, 255, 0) 260px
  );
  background-size: 101% calc(100% + 80px);
  background-position: top;
  transition: 0.5s ease;
  /* border: 1px solid white; */
  @media (${theme.sm}) {
    position: relative;
    height: auto;
    padding: 1rem 0;
  }
  h3 {
    font-weight: 700;
    transition: 0.3s ease;
    margin: 0;
    margin-bottom: 12px;
    @media (${theme.sm}) {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: start;
      text-align: left;
    }
  }
  .desc {
    width: 100%;
    text-align: left;
    transition: 0.3s ease;
    overflow: hidden;
    height: 0px;
    margin: 0;
    @media (${theme.sm}) {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      text-overflow: ellipsis;
      height: 80px;
      margin: 0;
      margin-top: 8px;
    }
  }

  .line {
    height: 2px;
    width: 50px;
    background: linear-gradient(
      to right,
      ${themeGet("colors.primary")},
      rgb(255, 253, 247)
    );
    transition: 0.3s;
    @media (${theme.md}) {
      display: none;
    }
  }
`
const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  @media (${theme.sm}) {
    flex-direction: column;
    width: 100%;
    align-items: start;
  }
  .date {
    font-size: 16px;
    min-width: 90px;
    margin: 0;
    &.none {
      display: none;
    }
  }
  .tags {
    flex: 0 1 calc(100% - 100px);
    position: relative;
    z-index: 33;
    display: flex;
    column-gap: 16px;
    overflow: hidden;
    flex-wrap: nowrap;
    flex-shrink: 1;
    height: 30px;
    &::after {
      content: "";
      position: absolute;
      height: 100%;
      width: 100%;
      background: linear-gradient(
        to left,
        ${themeGet("colors.background")},
        rgba(255, 255, 255, 0) 100px
      );
      z-index: 33;
    }

    @media (${theme.sm}) {
      flex-wrap: wrap;
      width: 100%;
      &::after {
        z-index: 0;
      }
    }
  }

  .tag {
    display: flex;
    margin: 0;
    white-space: nowrap;
    color: ${themeGet("colors.primary")};
    font-size: 16px;
    padding: 4px 0px;
    border-radius: 16px;
    opacity: 1;
    z-index: 33;
  }
`
const IconWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  p {
    margin: 0;
    color: ${theme.color.primary};
  }
`
